import { useAui, useAuiState } from "@assistant-ui/react";
import { type FC, useCallback, useRef } from "react";

import {
    type MidStreamChannel,
    useMidStreamRecoveryReload,
} from "@/hooks/chat/useMidStreamRecoveryReload";

import {
    sameChatOrganization,
    sameChatPrincipalSession,
    type ChatIdentitySnapshot,
} from "../adapters/chat-model-adapter";
import { loadConversationRepository } from "../utils/handleChatTurnCompleted";

export type MidStreamRecoveryReloaderProps = {
    /** Test override only. Production derives the id from runtime state. */
    getActiveThreadId?: () => string | undefined;
    channel?: MidStreamChannel;
    /**
     * Read once before the canonical fetch and again before the import. A
     * thread id alone cannot tell two organizations apart, and they can hold
     * the same conversation id.
     */
    getIdentitySnapshot?: () => ChatIdentitySnapshot;
};

/**
 * Re-read the displayed conversation after a post-admission stream interruption
 * so an open tab reflects the durable execution state.
 *
 * Mounted inside the runtime provider, which is the only place `aui.thread()`
 * refers to the thread the user is actually looking at: the per-thread history
 * providers sit ABOVE their own thread scope, so importing from there targets
 * the wrong thread. One subscriber for the active thread, not one per alive
 * thread.
 *
 * A failure on a thread that is not displayed is intentionally ignored -- the
 * history adapter re-reads that conversation when the user navigates back to it.
 */
export const MidStreamRecoveryReloader: FC<MidStreamRecoveryReloaderProps> = ({
    getActiveThreadId,
    channel,
    getIdentitySnapshot,
}) => {
    const aui = useAui();

    // The displayed conversation's server id, read from the same runtime state
    // assistant-ui itself uses. Deliberately NOT a hand-maintained ref: the
    // RuntimeProvider's remoteIdRef is assigned only when a thread is CREATED, so
    // it is undefined for any conversation opened from history -- which silently
    // disabled this entire entry path.
    const displayedThreadId = useAuiState((s) => {
        const mainId = s.threads.mainThreadId;
        return s.threads.threadItems.find((t) => t.id === mainId)?.remoteId;
    });
    const displayedRef = useRef(displayedThreadId);
    displayedRef.current = displayedThreadId;

    const resolveThreadId = useCallback(
        () => getActiveThreadId?.() ?? displayedRef.current,
        [getActiveThreadId]
    );

    const reload = useCallback(
        async (threadId: string) => {
            const captured = getIdentitySnapshot?.();
            const repository = await loadConversationRepository(threadId);
            // The runtime's main thread follows the route, so the import target
            // is whatever is displayed NOW -- not what was displayed when the
            // fetch started. If the user moved on, this repository belongs to a
            // conversation that is no longer on screen; dropping it is correct,
            // because navigating back re-reads through the history adapter.
            if (resolveThreadId() !== threadId) return;
            const current = getIdentitySnapshot?.();
            if (
                !sameChatOrganization(captured, current) ||
                !sameChatPrincipalSession(captured, current)
            ) {
                return;
            }
            aui.thread().import(repository);
        },
        [aui, resolveThreadId, getIdentitySnapshot]
    );

    useMidStreamRecoveryReload({
        getOwnThreadId: resolveThreadId,
        reload,
        channel,
    });

    return null;
};
